import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum PhoneNumberUseCase {
  Marketing = "marketing",
  Transactional = "transactional",
  Support = "support",
  Recover = "recover",
}

export enum PhoneNumberProvider {
  Telnyx = "telnyx",
  Bandwidth = "bandwidth",
  RedoIMessage = "redo-imessage",
}

export enum TextMessagingVerificationStatus {
  Unverified = "unverified",
  Pending = "pending",
  Verified = "verified",
  Rejected = "rejected",
}

export enum PortEventType {
  PortIn = "port-in",
  PortOut = "port-out",
}

export enum ProviderTransitionType {
  FullPort = "full-port",
  HostedMessaging = "hosted-messaging",
}

interface VoiceMetadata {
  billingGroupId: string;
  sipConnectionId: string;
}

/**
 * Used to track the transition of a phone number from one provider to another.
 * After initiating a transition, the true transition will randomly occur
 * sometime within the next two weeks. During that time, we need to be prepared
 * to send from either provider until the transition is fully complete.
 */
const ProviderTransitionSchema_ = z.object({
  toProvider: z.enum(PhoneNumberProvider),
  /** The type of transition - full port transfers ownership, hosted messaging only transfers messaging */
  transitionType: z.enum(ProviderTransitionType).optional(),
  startedAt: z.date(),
  portCompletedAt: z.date().nullish(),
});
export const ProviderTransitionSchema: z.ZodType<ProviderTransition> =
  ProviderTransitionSchema_;
export interface ProviderTransition extends z.infer<
  typeof ProviderTransitionSchema_
> {}

export const TRANSITION_PROBE_MESSAGE_TEXT =
  "__testing bandwidth messaging capability__";

export const TRANSITION_PROBE_TAG = "transition-probe";

/**
 * Schema for individual port history events
 */
const PortHistoryEventSchema_ = z.object({
  eventType: z.enum(PortEventType),
  provider: z.enum(PhoneNumberProvider),
  status: z.string(),
  timestamp: z.date(),
  rawWebhook: z.unknown().nullish(),
});
export const PortHistoryEventSchema: z.ZodType<PortHistoryEvent> =
  PortHistoryEventSchema_;
export interface PortHistoryEvent extends z.infer<
  typeof PortHistoryEventSchema_
> {}

export const PhoneNumberSchema = z.object({
  _id: zExt.objectId(),
  number: z.string(),
  team: zExt.objectId(),
  provider: createEnum(PhoneNumberProvider),
  useCase: createEnum(PhoneNumberUseCase),
  alphanumericSenderId: z.string().nullish(),
  messagingMetadata: z
    .object({
      verificationStatus: createEnum(TextMessagingVerificationStatus).default(
        TextMessagingVerificationStatus.Unverified,
      ),
      lastError: z.string().nullish(),
    })
    .optional(),
  voiceMetadata: z
    .object({ billingGroupId: z.string(), sipConnectionId: z.string() })
    .optional(),
  providerTransition: ProviderTransitionSchema.nullish(),
  portHistory: z.array(PortHistoryEventSchema).nullish(),
  telnyx: z
    .object({
      phoneNumberId: z.string().nullish(),
      messagingProfileId: z.string().nullish(),
      tollFreeVerificationId: z.string().nullish(),
      // Foreign key into telnyx_campaign_registrations (BIGINT serialized as
      // string). Lifecycle state (status, lastRejectionReason, etc.) lives in
      // that table, not duplicated here. See redo/cockroachdb/schema/
      // definitions/tables/marketing/telnyx-tcr.sql.
      tenDlcCampaignRegistrationId: z.string().nullish(),
    })
    .nullish(),
});

export type PhoneNumber = z.infer<typeof PhoneNumberSchema>;

/**
 * Minimal merchant-side phone identity used by the support text pipeline.
 *
 * Support routing only needs the number (to find/attach the conversation)
 * and the use case (to gate AI replies, marketing vs transactional behavior,
 * etc.). The full PhoneNumber document is overkill — and unavailable for
 * shared-number providers like Redo's Mac Mini iMessage gateway, where there
 * is no per-team PhoneNumber record to fetch.
 *
 * A full PhoneNumber structurally satisfies this shape, so existing callers
 * that already have one can pass it unchanged.
 */
export type MerchantPhoneRef = Pick<PhoneNumber, "number" | "useCase">;

export type MessagingMetadata = NonNullable<PhoneNumber["messagingMetadata"]>;

export type VoiceEnabledPhoneNumber = PhoneNumber & {
  voiceMetadata: VoiceMetadata;
};

export function isVoiceEnabledPhoneNumber(
  phoneNumber: PhoneNumber,
): phoneNumber is VoiceEnabledPhoneNumber {
  return !!phoneNumber.voiceMetadata;
}

const TOLL_FREE_AREA_CODES = new Set([
  "800",
  "888",
  "877",
  "866",
  "855",
  "844",
  "833",
]);

export function isTollFreeNumber(number: string): boolean {
  const digits = number.replace(/\D/g, "");
  const areaCode = digits.startsWith("1")
    ? digits.slice(1, 4)
    : digits.slice(0, 3);
  return TOLL_FREE_AREA_CODES.has(areaCode);
}

export function determineCountryCode(phoneNumber: string): string | undefined {
  if (phoneNumber.startsWith("+1")) {
    return "US";
  }
  // extend for other country codes as needed
  return undefined;
}

/**
 * All business registration types supported by Bandwidth's Toll-Free Verification API.
 * See https://dev.bandwidth.com/apis/messaging-apis/toll-free-verification/
 */
export enum BusinessRegistrationType {
  ABN = "ABN",
  ACN = "ACN",
  BRN = "BRN",
  CBN = "CBN",
  CIF = "CIF",
  CNPJ = "CNPJ",
  CRN = "CRN",
  EIN = "EIN",
  NEQ = "NEQ",
  NIF = "NIF",
  NZBN = "NZBN",
  OTHER = "OTHER",
  PROVINCIAL_NUMBER = "PROVINCIAL_NUMBER",
  SIREN = "SIREN",
  SIRET = "SIRET",
  UID = "UID",
  UST_IDNR = "UST_IDNR",
  VAT = "VAT",
}

/** Human-readable labels for each registration type. */
export const BUSINESS_REGISTRATION_TYPE_LABELS: Record<
  BusinessRegistrationType,
  string
> = {
  ABN: "ABN — Australian Business Number",
  ACN: "ACN — Australian Company Number",
  BRN: "BRN — Hong Kong Business Registration Number",
  CBN: "CBN — Canada Business Number",
  CIF: "CIF — Spain Tax Identification Code",
  CNPJ: "CNPJ — Brazil National Registry of Legal Entities",
  CRN: "CRN — Company Registration Number (UK/HK)",
  EIN: "EIN — US Employer Identification Number",
  NEQ: "NEQ — Québec Enterprise Number",
  NIF: "NIF — Spain Tax Identification Number",
  NZBN: "NZBN — New Zealand Business Number",
  OTHER: "OTHER — Other (provide country code)",
  PROVINCIAL_NUMBER: "Provincial Number (Canada)",
  SIREN: "SIREN — French Company ID (9 digits)",
  SIRET: "SIRET — French Establishment ID (14 digits)",
  UID: "UID — Switzerland Company ID",
  UST_IDNR: "UST-IdNr — German VAT ID",
  VAT: "VAT — Value Added Tax Number (UK/IE/BR/NL)",
};

/**
 * Maps each registration type to its supported issuing countries (ISO-3166-1 alpha-3).
 * "OTHER" requires explicit country specification.
 */
export const BUSINESS_REGISTRATION_TYPE_COUNTRIES: Record<
  BusinessRegistrationType,
  string[]
> = {
  ABN: ["AUS"],
  ACN: ["AUS"],
  BRN: ["HKG"],
  CBN: ["CAN"],
  CIF: ["ESP"],
  CNPJ: ["BRA"],
  CRN: ["GBR", "HKG"],
  EIN: ["USA"],
  NEQ: ["CAN"],
  NIF: ["ESP"],
  NZBN: ["NZL"],
  OTHER: [],
  PROVINCIAL_NUMBER: ["CAN"],
  SIREN: ["FRA"],
  SIRET: ["FRA"],
  UID: ["CHE"],
  UST_IDNR: ["DEU"],
  VAT: ["GBR", "IRL", "BRA", "NLD"],
};

const TollFreePhoneNumberProvisionRequest_ = z.object({
  teamId: z.string(),
  useCase: z.enum(PhoneNumberUseCase),
  existingPhoneNumber: z.string().optional(),
  businessName: z.string(),
  legalBusinessName: z.string(),
  website: z.string(),
  businessAddress: z.object({
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  }),
  businessContact: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phone: z.string(),
  }),
  businessRegistrationNumber: z.string(),
  businessRegistrationType: z.enum(BusinessRegistrationType),
  businessRegistrationIssuingCountry: z.string(),
  optInScreenshotBase64: z.string(),
});

export const TollFreePhoneNumberProvisionRequestSchema: z.ZodType<TollFreePhoneNumberProvisionRequest> =
  TollFreePhoneNumberProvisionRequest_;
export interface TollFreePhoneNumberProvisionRequest extends z.infer<
  typeof TollFreePhoneNumberProvisionRequest_
> {}

const TollFreePhoneNumberProvisionResponse_ = z.object({
  useCase: z.enum(PhoneNumberUseCase),
  status: z.enum(["success", "failed"]),
  phoneNumber: z.string().optional(),
  message: z.string(),
});

export const TollFreePhoneNumberProvisionResponseSchema: z.ZodType<TollFreePhoneNumberProvisionResponse> =
  TollFreePhoneNumberProvisionResponse_;
export interface TollFreePhoneNumberProvisionResponse extends z.infer<
  typeof TollFreePhoneNumberProvisionResponse_
> {}

const TollFreeVerificationStatusResponse_ = z.object({
  phoneNumber: z.string(),
  status: z.enum([
    "pending",
    "verified",
    "unverified",
    "rejected",
    "not_found", // Bandwidth 404 — no verification record (expected for Telnyx migrations)
    "unknown", // API error or unable to fetch status
  ]),
  message: z.string(),
});

export const TollFreeVerificationStatusResponseSchema: z.ZodType<TollFreeVerificationStatusResponse> =
  TollFreeVerificationStatusResponse_;
export interface TollFreeVerificationStatusResponse extends z.infer<
  typeof TollFreeVerificationStatusResponse_
> {}
